<template>
  <div class="form-field-container">
    <label v-if="showLabel" :for="id" class="form-label">{{ label }}</label>
    <div v-if="showRequired" class="required-mark-container">
      <span v-if="required" class="required-mark">必須</span>
    </div>
    <div class="select-container" :class="`width-${size}`">
      <div 
        class="select-display" 
        @click="toggleDropdown"
        :class="{ 'is-open': isOpen }"
      >
        {{ displayText }}
        <span class="dropdown-arrow">▼</span>
      </div>
      <div v-if="isOpen" class="select-dropdown">
        <input 
          type="text" 
          v-model="searchText" 
          class="search-input" 
          placeholder="検索..."
        />
        <div 
          v-for="option in filteredOptions" 
          :key="option.value" 
          class="select-option"
          @click="selectOption(option)"
          :class="{ 
            'parent-category': option.value && option.value.toString().startsWith('parent_'),
            'child-category': option.value && !option.value.toString().startsWith('parent_') && option.value !== ''
          }"
        >
          {{ option.label }}
        </div>
        <div v-if="filteredOptions.length === 0" class="no-results">
          該当項目なし
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FormSelectSearchField',
  props: {
    label: {
      type: String,
      required: true
    },
    id: {
      type: String,
      required: true
    },
    modelValue: {
      type: [String, Number],
      default: ''
    },
    required: {
      type: Boolean,
      default: false
    },
    showLabel: {
      type: Boolean,
      default: true
    },
    showRequired: {
      type: Boolean,
      default: true
    },
    options: {
      type: Array,
      default: () => []
    },
    placeholder: {
      type: String,
      default: '選択してください'
    },
    size: {
      type: String,
      default: 'small',
      validator: value => ['small', 'medium', 'large'].includes(value)
    }
  },
  data() {
    return {
      isOpen: false,
      searchText: '',
      selectedLabel: '',
      currentIndex: -1
    };
  },
  computed: {
    displayText() {
      if (this.modelValue) {
        const selectedOption = this.options.find(option => option.value === this.modelValue);
        return selectedOption ? selectedOption.label.trim() : this.placeholder;
      }
      return this.placeholder;
    },
    filteredOptions() {
      // 検索テキストが空の場合
      if (!this.searchText) {
        // 「選択してください」オプションを先頭に追加
        const placeholderOption = { value: '', label: this.placeholder };
        return [placeholderOption, ...this.options];
      }
      
      // 検索テキストがある場合は前方一致でフィルタリング
      return this.options.filter(option => 
        option.label.toLowerCase().includes(this.searchText.toLowerCase())
      );
    }
  },
  methods: {
    toggleDropdown() {
      this.isOpen = !this.isOpen;
      if (!this.isOpen) {
        this.searchText = '';
        return;
      }
      this.searchText = this.modelValue ? this.displayText : ''; // 未選択の場合は設定しない
      this.$nextTick(() => {
        const searchInput = this.$el.querySelector('.search-input');
        if (!searchInput) return;
        searchInput.focus();
        searchInput.addEventListener('keydown', this.handleEnterKey); // Enterキーのイベントリスナーを追加
        searchInput.addEventListener('keydown', this.handleArrowKeys); // 矢印キーのイベントリスナーを追加
      });
    },
    handleEnterKey(event) {

      if (event.key !== 'Enter') return;

      const currentOption = 0 <= this.currentIndex && this.currentIndex < this.filteredOptions.length
        ? this.filteredOptions[this.currentIndex]
        : this.filteredOptions[0];

      if (currentOption) {
        this.selectOption(currentOption);
      }
      event.preventDefault(); // デフォルトのEnter動作を防止
    },
    handleArrowKeys(event) {
      const options = this.$el.querySelectorAll('.select-option');
      if (!options.length) return;
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

      if (!this.currentIndex && this.currentIndex !== 0) {
        this.currentIndex = 0;
      }

      if (event.key === 'ArrowDown') {
        this.currentIndex = (this.currentIndex + 1) % options.length;
      } else if (event.key === 'ArrowUp') {
        this.currentIndex = (this.currentIndex - 1 + options.length) % options.length;
      }

      // 選択されたオプションを強調表示
      options.forEach((option, index) => {
        if (index === this.currentIndex) {
          option.classList.add('highlight');
        } else {
          option.classList.remove('highlight');
        }
      });

      // スクロールして選択されたオプションを表示
      options[this.currentIndex]?.scrollIntoView({ block: 'nearest' });
      event.preventDefault();
    },
    selectOption(option) {
      this.$emit('update:modelValue', option.value);
      this.selectedLabel = option.label;
      this.isOpen = false;
      this.searchText = '';
    },
    handleOutsideClick() {
        if (this.$el.contains(document.activeElement)) return;
        this.isOpen = false;
    }
  },
  mounted() {
    document.addEventListener('click', this.handleOutsideClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick);
  }
};
</script>

<style scoped>
.form-field-container {
  display: flex;
  align-items: center;
  background-color: #FFF;
  padding: 10px 30px;
  margin: 5px;
  border-radius: 4px;
}

.form-label {
  min-width: 180px;
  font-weight: 700;
  flex: 0 0 180px;
}

.required-mark-container {
  width: 40px;
  margin-right: 10px;
  flex-shrink: 0;
}

.required-mark {
  background-color: #ff0000;
  color: white;
  padding: 2px 5px;
  border-radius: 3px;
  font-size: 12px;
  width: 100%;
  text-align: center;
  display: inline-block;
}

.select-container {
  min-width: 200px;
  position: relative;
}

.select-container.width-small {
  flex: 0 0 200px; /* Small width */
}

.select-container.width-medium {
  flex: 0 0 400px; /* Medium width */
}

.select-container.width-large {
  flex: 1; /* Large width (default) */
}

.select-display {
  width: 100%;
  padding: 8px;
  border: 2px solid #195983;
  border-radius: 4px;
  box-sizing: border-box;
  background-color: white;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.select-display.is-open {
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

.dropdown-arrow {
  color: #195983;
  font-size: 12px;
}

.select-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  max-height: 300px;
  overflow-y: auto;
  background-color: white;
  border: 1px solid #ccc;
  border-radius: 0 0 4px 4px;
  z-index: 10;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
}

.search-input {
  width: 100%;
  padding: 6px;
  border: 1px solid #ccc;
  box-sizing: border-box;
}

.select-option.child-category {
  padding-left: 24px;
}

.select-option {
  padding: 8px;
  cursor: pointer;
}

.select-option:hover {
  background-color: #f0f0f0;
}

.select-option.parent-category {
  font-weight: bold;
}

.no-results {
  padding: 8px;
  color: #999;
  text-align: center;
}

.highlight {
  background-color: #d3d3d3;
}
</style>
